import { useState } from 'react';
import { TablePagination } from '../../components/TablePagination';
import { Group, Badge, Table, Button, Menu, Select } from '@mantine/core';
import { IconCrown, IconChevronDown } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import api from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { TableToolbar } from '../../components/TableToolbar';
import { TableSkeleton } from '../../components/TableSkeleton';
import { FetchOverlay } from '../../components/FetchOverlay';
import { EmptyState } from '../../components/EmptyState';
import { FeatureFrozen } from '../../components/FeatureFrozen';
import { usePagedQuery } from '../../hooks/usePagedQuery';
import { formatDate, formatPhone } from '../../lib/format';
import { PREMIUM_UI_ENABLED } from '../../lib/features';
import { useTranslation } from 'react-i18next';

interface ParentRow {
  id: string; fullName: string; phone?: string | null; tenantId: string; tenantName: string;
  premiumUntil?: string | null; isActive: boolean; _count: { children: number; giftsReceived: number };
}
interface TenantOpt { id: string; name: string }

const active = (u?: string | null) => !!u && new Date(u) > new Date();

/**
 * Marshrut komponenti (`/saas-premium`).
 *
 * Premium muzlatilganda (`lib/features.ts` → `PREMIUM_UI_ENABLED = false`) bu sahifa
 * AMALDA ISHLAMAYDI: backend `PATCH /superadmin/parents/:id` da `assertPremiumEnabled()`
 * turibdi, ya'ni "premium berish/bekor qilish" tugmalari 403 qaytaradi. Shuning uchun
 * yon menyudan olib tashlangan, URL bilan kirilsa esa "vaqtincha o'chirilgan" ko'rinadi.
 * Tekshiruv hook'lardan OLDIN — muzlatilgan paytda ortiqcha so'rov ketmasin.
 */
export default function SaasPremium() {
  if (!PREMIUM_UI_ENABLED) return <FeatureFrozen titleKey="saasPremiumPage.title" />;
  return <SaasPremiumTable />;
}

/** Superadmin — barcha maktablar bo'ylab Premium to'lovlar nazorati. */
function SaasPremiumTable() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [tenantId, setTenantId] = useState<string>('');
  const [premium, setPremium] = useState<string>('');
  const [sort, setSort] = useState('fullName:asc');

  const { data: tenants = [] } = useQuery({
    queryKey: ['sa-tenants-opt'],
    queryFn: async () => (await api.get<TenantOpt[]>('/superadmin/tenants')).data,
  });

  const parents = usePagedQuery<ParentRow>('sa-parents', '/superadmin/parents', {
    q: q || undefined, tenantId: tenantId || undefined, premium: premium || undefined, sort,
  });
  // Filtrlangan bo'sh natija ma'lumot umuman yo'qligidan farqli matn ko'rsatadi
  const filterActive = !!q || !!tenantId || !!premium;

  const grantMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api.patch(`/superadmin/parents/${id}`, body),
    onSuccess: () => { notifications.show({ color: 'green', message: t('saasPremiumPage.premiumUpdated') }); qc.invalidateQueries({ queryKey: ['sa-parents'] }); },
  });

  return (
    <>
      <PageHeader title={t('saasPremiumPage.title')} subtitle={t('saasPremiumPage.subtitle')} />
      <TableToolbar
        search={search} onSearch={(v) => { setSearch(v); parents.setPage(1); }} searchPlaceholder={t('saasPremiumPage.searchPlaceholder')}
        sort={sort} onSort={setSort}
        sortOptions={[
          { value: 'fullName:asc', label: t('saasPremiumPage.sortNameAsc') },
          { value: 'premiumUntil:asc', label: t('saasPremiumPage.sortPremiumAsc') },
          { value: 'premiumUntil:desc', label: t('saasPremiumPage.sortPremiumDesc') },
          { value: 'createdAt:desc', label: t('saasPremiumPage.sortNewest') },
        ]}
      >
        <Select placeholder={t('saasPremiumPage.school')} data={[{ value: '', label: t('saasPremiumPage.allSchools') }, ...tenants.map((tn) => ({ value: tn.id, label: tn.name }))]}
          value={tenantId} onChange={(v) => { setTenantId(v ?? ''); parents.setPage(1); }} w={200} clearable />
        <Select placeholder={t('parentDetail.subscription')} data={[{ value: '', label: t('saasPremiumPage.all') }, { value: 'yes', label: t('nav.premium') }, { value: 'no', label: t('saasPremiumPage.regular') }]}
          value={premium} onChange={(v) => { setPremium(v ?? ''); parents.setPage(1); }} w={140} clearable />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconCrown} color="yellow" p={0} pos="relative">
        {parents.isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : !parents.items.length ? (
          <EmptyState icon={IconCrown} title={filterActive ? t('common.noMatch') : t('parentDetail.notFound')} />
        ) : (
          <Table.ScrollContainer minWidth={900}>
            <FetchOverlay visible={parents.fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('studentDetailPage.parent')}</Table.Th>
                  <Table.Th>{t('saasPremiumPage.school')}</Table.Th>
                  <Table.Th>{t('fields.phone')}</Table.Th>
                  <Table.Th w={110}>{t('parentDetail.children')}</Table.Th>
                  <Table.Th w={110}>{t('parentDetail.giftsReceived')}</Table.Th>
                  <Table.Th>{t('parentDetail.subscription')}</Table.Th>
                  <Table.Th w={160}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {parents.items.map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td fw={500}>
                      <Group gap={6}>{p.fullName}{active(p.premiumUntil) && <IconCrown size={15} color="var(--mantine-color-yellow-6)" />}</Group>
                    </Table.Td>
                    <Table.Td><Badge variant="light" color="grape">{p.tenantName}</Badge></Table.Td>
                    <Table.Td>{formatPhone(p.phone)}</Table.Td>
                    <Table.Td>{p._count?.children || '—'}</Table.Td>
                    <Table.Td>{p._count?.giftsReceived || '—'}</Table.Td>
                    <Table.Td>
                      {active(p.premiumUntil)
                        ? <Badge color="yellow" variant="light">{formatDate(p.premiumUntil!)} {t('parentDetail.until')}</Badge>
                        : <Badge color="gray" variant="light">{t('saasPremiumPage.regular')}</Badge>}
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end">
                        <Menu.Target><Button size="xs" variant="light" color="yellow" rightSection={<IconChevronDown size={14} />}>{t('nav.premium')}</Button></Menu.Target>
                        <Menu.Dropdown>
                          {[1, 3, 6, 12].map((m) => (
                            <Menu.Item key={m} onClick={() => grantMut.mutate({ id: p.id, body: { premiumMonths: m } })}>{m} {t('parentDetail.monthsGive')}</Menu.Item>
                          ))}
                          {active(p.premiumUntil) && <Menu.Item color="red" onClick={() => grantMut.mutate({ id: p.id, body: { premiumOff: true } })}>{t('common.cancel')}</Menu.Item>}
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={parents.page}
        pages={parents.pages}
        total={parents.total}
        limit={parents.limit}
        onPage={parents.setPage}
        onLimit={parents.setLimit}
      />
    </>
  );
}
